import { useCallback, useRef, useState } from 'react';

const LIMIT = 100;
const COALESCE_MS = 1200;

/**
 * Design state ({ text_fields, elements }) with an undo/redo stack.
 *
 * `change(updater, { key })`: consecutive changes with the same `key` within a
 * short window (typing in one box, dragging one slider, one drag gesture) are
 * merged into a single undo step.
 */
export const useDesignHistory = (initial) => {
  const [design, setDesign] = useState(initial);
  const current = useRef(initial);
  const past = useRef([]);
  const future = useRef([]);
  const last = useRef({ key: null, at: 0 });
  const [counts, setCounts] = useState({ undo: 0, redo: 0 });

  const sync = () => setCounts({ undo: past.current.length, redo: future.current.length });

  const commit = useCallback((next) => {
    current.current = next;
    setDesign(next);
  }, []);

  const change = useCallback(
    (updater, { key } = {}) => {
      const prev = current.current;
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (!next || next === prev) return false;
      const now = Date.now();
      const merge = key && last.current.key === key && now - last.current.at < COALESCE_MS;
      if (!merge) {
        past.current = [...past.current, prev].slice(-LIMIT);
      }
      future.current = [];
      last.current = { key: key || null, at: now };
      commit(next);
      sync();
      return true;
    },
    [commit]
  );

  /** End the current merge window so the next change starts a new undo step. */
  const breakMerge = useCallback(() => {
    last.current = { key: null, at: 0 };
  }, []);

  const undo = useCallback(() => {
    if (!past.current.length) return false;
    const prev = past.current[past.current.length - 1];
    past.current = past.current.slice(0, -1);
    future.current = [current.current, ...future.current];
    last.current = { key: null, at: 0 };
    commit(prev);
    sync();
    return true;
  }, [commit]);

  const redo = useCallback(() => {
    if (!future.current.length) return false;
    const [next, ...rest] = future.current;
    future.current = rest;
    past.current = [...past.current, current.current];
    last.current = { key: null, at: 0 };
    commit(next);
    sync();
    return true;
  }, [commit]);

  /** Replace the design and clear history (after loading). */
  const reset = useCallback(
    (next) => {
      past.current = [];
      future.current = [];
      last.current = { key: null, at: 0 };
      commit(next);
      sync();
    },
    [commit]
  );

  return {
    design,
    designRef: current,
    change,
    undo,
    redo,
    reset,
    breakMerge,
    canUndo: counts.undo > 0,
    canRedo: counts.redo > 0,
  };
};

export default useDesignHistory;
